import { ObjectPath } from "components/Form/components/SchemaForm/SchemaForm.types";
import { createContext, useContext, useRef } from "react";

let nextArrayItemId = 0;

/** Creates a unique id for one array row (React key + drag id). */
export function createArrayItemId(): string {
    nextArrayItemId += 1;
    return `schema-form-array-item-${nextArrayItemId}`;
}

/** Stable string key for a form data path in the id registry. */
export function objectPathKey(dataPath: ObjectPath): string {
    return dataPath.length === 0 ? "" : dataPath.join("\0");
}

export interface SchemaFormArrayItemIdApi {
    /** Returns ids for `dataPath`, growing or resetting when length changes. */
    getItemIds(dataPath: ObjectPath, length: number): readonly string[];
    /** Appends one id (call before adding a row). */
    addItemId(dataPath: ObjectPath): void;
    /** Removes the id at `index` (call when deleting a row). */
    removeItemId(dataPath: ObjectPath, index: number): void;
    /** Reorders ids after drag-and-drop. */
    reorderItemIds(dataPath: ObjectPath, fromIndex: number, toIndex: number): void;
    /** Replaces the full id list (e.g. after drag ends). */
    replaceItemIds(dataPath: ObjectPath, itemIds: readonly string[]): void;
    /** Clears all tracked ids (e.g. when loading different form data). */
    resetAll(): void;
}

export function createArrayItemIdRegistry(): SchemaFormArrayItemIdApi {
    const registry = new Map<string, string[]>();

    const getOrCreate = (dataPath: ObjectPath): string[] => {
        const key = objectPathKey(dataPath);
        let ids = registry.get(key);
        if (!ids) {
            ids = [];
            registry.set(key, ids);
        }
        return ids;
    };

    return {
        getItemIds(dataPath: ObjectPath, length: number): readonly string[] {
            const ids = getOrCreate(dataPath);
            if (ids.length === length) {
                return ids;
            }
            if (ids.length < length) {
                while (ids.length < length) {
                    ids.push(createArrayItemId());
                }
                return ids;
            }
            // Length shrank without an explicit remove (e.g. code-editor sync) — re-key all rows.
            ids.length = 0;
            for (let i = 0; i < length; i++) {
                ids.push(createArrayItemId());
            }
            return ids;
        },

        addItemId(dataPath: ObjectPath): void {
            getOrCreate(dataPath).push(createArrayItemId());
        },

        removeItemId(dataPath: ObjectPath, index: number): void {
            const ids = registry.get(objectPathKey(dataPath));
            if (ids) {
                ids.splice(index, 1);
            }
        },

        reorderItemIds(dataPath: ObjectPath, fromIndex: number, toIndex: number): void {
            const ids = registry.get(objectPathKey(dataPath));
            if (!ids) {
                return;
            }
            const [moved] = ids.splice(fromIndex, 1);
            ids.splice(toIndex, 0, moved);
        },

        replaceItemIds(dataPath: ObjectPath, itemIds: readonly string[]): void {
            registry.set(objectPathKey(dataPath), [...itemIds]);
        },

        resetAll(): void {
            registry.clear();
        },
    };
}

export function useSchemaFormArrayItemIdRegistry(): SchemaFormArrayItemIdApi {
    const registryRef = useRef<SchemaFormArrayItemIdApi>();
    if (!registryRef.current) {
        registryRef.current = createArrayItemIdRegistry();
    }
    return registryRef.current;
}

const SchemaFormArrayItemIdContext = createContext<SchemaFormArrayItemIdApi | null>(null);

export const SchemaFormArrayItemIdProvider = ({
    value,
    children,
}: {
    value: SchemaFormArrayItemIdApi;
    children: React.ReactNode;
}) => (
    <SchemaFormArrayItemIdContext.Provider value={value}>
        {children}
    </SchemaFormArrayItemIdContext.Provider>
);

/**
 * Returns the shared array-item id registry when inside SchemaFormArrayItemIdProvider,
 * otherwise a registry local to the calling component tree.
 */
export function useSchemaFormArrayItemIds(): SchemaFormArrayItemIdApi {
    const context = useContext(SchemaFormArrayItemIdContext);
    const localRegistry = useSchemaFormArrayItemIdRegistry();
    return context ?? localRegistry;
}
